import { z } from 'zod';

/**
 * Canonical domain vocabularies. Import sources (for example Nara Baby) are normalised
 * to these values; raw source values are retained only as import metadata.
 */

export const feedingMethodSchema = z.enum(['breast', 'bottle', 'solid']);
export type FeedingMethod = z.infer<typeof feedingMethodSchema>;

export const breastSideSchema = z.enum(['left', 'right', 'both']);
export type BreastSide = z.infer<typeof breastSideSchema>;

export const milkTypeSchema = z.enum(['breast_milk', 'formula', 'mixed', 'other']);
export type MilkType = z.infer<typeof milkTypeSchema>;

export const nappyTypeSchema = z.enum(['wet', 'dirty', 'mixed', 'dry']);
export type NappyType = z.infer<typeof nappyTypeSchema>;

export const stoolColourSchema = z.enum([
    'yellow',
    'mustard',
    'green',
    'brown',
    'black',
    'red',
    'white',
    'other',
]);
export type StoolColour = z.infer<typeof stoolColourSchema>;

export const stoolTextureSchema = z.enum([
    'runny',
    'soft',
    'seedy',
    'mucousy',
    'formed',
    'pebbles',
    'other',
]);
export type StoolTexture = z.infer<typeof stoolTextureSchema>;

export const sleepKindSchema = z.enum(['nap', 'night', 'unspecified']);
export type SleepKind = z.infer<typeof sleepKindSchema>;

export const milestoneCategorySchema = z.enum([
    'motor',
    'language',
    'social',
    'cognitive',
    'first',
    'other',
]);
export type MilestoneCategory = z.infer<typeof milestoneCategorySchema>;

export const doseUnitSchema = z.enum(['ml', 'mg', 'drops', 'tablet']);
export type DoseUnit = z.infer<typeof doseUnitSchema>;

export const imageContentTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/webp']);
export type ImageContentType = z.infer<typeof imageContentTypeSchema>;

export const reminderKindSchema = z.enum(['feed', 'pump', 'medication', 'sleep', 'custom']);
export type ReminderKind = z.infer<typeof reminderKindSchema>;

/** A clock time in the family zone, `HH:mm` on a 24-hour clock. */
export const timeOfDaySchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time like 07:30');
export type TimeOfDay = z.infer<typeof timeOfDaySchema>;

export const childSexSchema = z.enum(['female', 'male', 'unspecified']);
export type ChildSex = z.infer<typeof childSexSchema>;

export const familyRoleSchema = z.enum(['owner', 'caregiver', 'viewer']);
export type FamilyRole = z.infer<typeof familyRoleSchema>;

/** Roles that can be granted through an invitation. Ownership is assigned only at family creation or by an owner changing a member. */
export const invitationRoleSchema = z.enum(['caregiver', 'viewer']);
export type InvitationRole = z.infer<typeof invitationRoleSchema>;

const familyRoleRank: Record<FamilyRole, number> = {
    viewer: 0,
    caregiver: 1,
    owner: 2,
};

/** True when `role` is at least as privileged as `minimum`. */
export function familyRoleAtLeast(role: FamilyRole, minimum: FamilyRole): boolean {
    return familyRoleRank[role] >= familyRoleRank[minimum];
}
